import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { aiSettingsApi } from "@/lib/api/ai-settings";
import { ApiError } from "@/lib/api/client";
import type { Job } from "@/lib/api/mailbox";
import { mailboxKeys } from "./use-mailbox";

/** A step held longer than this is assumed dead; the server lets it be retaken. */
const STALE_STEP_MS = 4 * 60_000 + 5_000;

/**
 * Drives a request interview that runs on the person's API key: whenever the
 * job is waiting on the AI, asks the server for the next step. The server only
 * acts when there's something to do, so a repeat call (another tab, a reload)
 * is harmless. Jobs on a paired computer are driven by its daemon instead.
 *
 * Returns an error message when a step couldn't be requested.
 */
export function useKeyInterviewDriver(job: Job | undefined): string | null {
  const queryClient = useQueryClient();
  const inFlight = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Only request conversations are driven from here; cloud builds run on their own.
  const isCloud = job?.runsOn === "cloud" && job.type === "epic_request";
  const status = job?.status;
  const turnCount = job?.turns.length ?? 0;
  const stale = status === "claimed" && !!job && Date.now() - new Date(job.updatedAt).getTime() > STALE_STEP_MS;
  const needsStep = isCloud && (status === "running" || stale);
  const stepKey = job ? `${job.id}:${String(turnCount)}:${status ?? ""}` : null;

  useEffect(() => {
    if (!job || !needsStep || !stepKey || inFlight.current === stepKey) return;
    inFlight.current = stepKey;
    setError(null);
    aiSettingsApi
      .advanceKeyInterview(job.id)
      .then((response) => {
        queryClient.setQueryData(mailboxKeys.job(job.id), response.data);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : "Couldn't reach CompBox to continue the interview.");
        // Allow a retry on the next poll.
        inFlight.current = null;
      });
  }, [job, needsStep, stepKey, queryClient]);

  return error;
}
